import { useState, useEffect, useRef } from 'react'
import { isUserEditor, isUserAdmin } from '../firebase/auth'
import {
  createTopic,
  subscribeToTopics,
  updateTopic,
  deleteTopic,
  markTopicAsUsed,
  markTopicAsUnused
} from '../firebase/topics'
import { subscribeToProducts } from '../firebase/products'
import { addDoc, collection, doc } from 'firebase/firestore'
import { db } from '../firebase/config'
import './WebsiteArticles.css'

const WebsiteArticles = ({ user }) => {
  const [isEditor, setIsEditor] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('articles')
  
  // Topics state
  const [topics, setTopics] = useState([])
  const [products, setProducts] = useState([])
  const [showTopicForm, setShowTopicForm] = useState(false)
  const [editingTopic, setEditingTopic] = useState(null)
  const [topicText, setTopicText] = useState('')
  const [topicCategory, setTopicCategory] = useState('')
  const [topicError, setTopicError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState(null)
  const [importCategory, setImportCategory] = useState('')
  const [importText, setImportText] = useState('')
  const [showTextImport, setShowTextImport] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [topicToDelete, setTopicToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [selectedTopic, setSelectedTopic] = useState(null)
  const [articlesProductFilter, setArticlesProductFilter] = useState('')
  
  // Editing state for available topics
  const [editingFields, setEditingFields] = useState({
    filename: '',
    htmloutput: '',
    keywords: '',
    description: ''
  })
  const [savingFields, setSavingFields] = useState(false)
  const [fieldError, setFieldError] = useState(null)
  
  // Sitemap state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString())

  // Ref for HTML preview – scroll to top when switching topics
  const htmlPreviewRef = useRef(null)

  useEffect(() => {
    const checkUserStatus = async () => {
      if (user) {
        const [editorStatus, adminStatus] = await Promise.all([
          isUserEditor(user),
          isUserAdmin(user)
        ])
        setIsEditor(editorStatus)
        setIsAdmin(adminStatus)
      }
      setLoading(false)
    }
    checkUserStatus()
  }, [user])

  useEffect(() => {
    if (isEditor && (activeTab === 'topics' || activeTab === 'articles' || activeTab === 'sitemap')) {
      // Subscribe to topics in real-time
      const unsubscribeTopics = subscribeToTopics((topicsList) => {
        setTopics(topicsList)
        // Reset to first page when topics change
        setCurrentPage(1)
      })
      
      // Subscribe to products in real-time
      const unsubscribeProducts = subscribeToProducts((productsList) => {
        setProducts(productsList)
      })
      
      return () => {
        unsubscribeTopics()
        unsubscribeProducts()
      }
    }
  }, [isEditor, activeTab])

  // Calculate stats
  const totalTopics = topics.length
  const unprocessedTopics = topics.filter(t => t.status === 'unprocessed').length
  const preProcessingTopics = topics.filter(t => t.status === 'pre-processing').length
  const availableTopics = topics.filter(t => t.status === 'available').length
  const approvedTopics = topics.filter(t => t.status === 'approved').length
  const rejectedTopics = topics.filter(t => t.status === 'rejected').length
  const errorTopics = topics.filter(t => t.status === 'error').length
  
  // Filter available topics for articles tab
  const availableTopicsList = topics
    .filter(t => t.status === 'available')
    .filter(t => !articlesProductFilter || t.category === articlesProductFilter)
  
  // Filter published topics for sitemap (check for 'published' status)
  const publishedTopics = topics.filter(t => t.status === 'published')
  
  // Get unique years from published topics
  const getAvailableYears = () => {
    const years = new Set()
    publishedTopics.forEach(topic => {
      if (topic.updatedAt) {
        const date = topic.updatedAt.toDate ? topic.updatedAt.toDate() : new Date(topic.updatedAt)
        if (!isNaN(date.getTime())) {
          years.add(date.getFullYear().toString())
        }
      }
    })
    const sortedYears = Array.from(years).sort((a, b) => parseInt(b) - parseInt(a))
    return sortedYears.length > 0 ? sortedYears : [new Date().getFullYear().toString()]
  }
  
  // Filter published topics by selected year
  const getSitemapTopics = () => {
    return publishedTopics.filter(topic => {
      if (!topic.updatedAt) return false
      const date = topic.updatedAt.toDate ? topic.updatedAt.toDate() : new Date(topic.updatedAt)
      if (isNaN(date.getTime())) return false
      return date.getFullYear().toString() === selectedYear
    }).sort((a, b) => {
      // Sort by updatedAt descending
      const dateA = a.updatedAt.toDate ? a.updatedAt.toDate() : new Date(a.updatedAt)
      const dateB = b.updatedAt.toDate ? b.updatedAt.toDate() : new Date(b.updatedAt)
      return dateB - dateA
    })
  }
  
  // Format date as YYYY-MM-DD
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    if (isNaN(date.getTime())) return 'N/A'
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Extract href URLs from HTML string for quick checking
  const getHrefsFromHtml = (html) => {
    if (!html || typeof html !== 'string') return []
    const re = /href\s*=\s*["']([^"']+)["']/gi
    const urls = []
    let m
    while ((m = re.exec(html)) !== null) urls.push(m[1])
    return [...new Set(urls)]
  }

  // Update selected year when available years change
  useEffect(() => {
    if (activeTab === 'sitemap' && (isEditor || isAdmin)) {
      const availableYears = getAvailableYears()
      setSelectedYear(prevYear => {
        if (availableYears.length > 0 && !availableYears.includes(prevYear)) {
          return availableYears[0]
        }
        return prevYear
      })
    }
  }, [topics, activeTab, isEditor, isAdmin])

  // Calculate pagination
  const totalPages = Math.ceil(totalTopics / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentPageTopics = topics.slice(startIndex, endIndex)

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleTopicFormChange = (e) => {
    const { name, value } = e.target
    if (name === 'topicText') {
      setTopicText(value)
    } else if (name === 'topicCategory') {
      setTopicCategory(value)
    }
    setTopicError(null)
  }

  const handleTopicSubmit = async (e) => {
    e.preventDefault()
    setTopicError(null)

    if (!topicText.trim()) {
      setTopicError('Topic text is required')
      return
    }

    setSubmitting(true)
    try {
      const categoryId = topicCategory || null
      if (editingTopic) {
        // Update existing topic
        await updateTopic(editingTopic.id, { 
          text: topicText.trim(),
          category: categoryId
        })
        setEditingTopic(null)
      } else {
        // Create new topic
        await createTopic(topicText.trim(), categoryId)
      }
      setTopicText('')
      setTopicCategory('')
      setShowTopicForm(false)
    } catch (err) {
      console.error('Error saving topic:', err)
      setTopicError(err.message || 'Failed to save topic. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleStartEdit = (topic) => {
    if (topic.status === 'used') {
      setTopicError('Cannot edit a topic that has been used')
      return
    }
    setEditingTopic(topic)
    setTopicText(topic.text)
    setTopicCategory(topic.category || '')
    setShowTopicForm(true)
    setTopicError(null)
  }

  const handleCancelEdit = () => {
    setEditingTopic(null)
    setTopicText('')
    setTopicCategory('')
    setShowTopicForm(false)
    setTopicError(null)
  }

  const handleDeleteTopic = (topic) => {
    setTopicToDelete(topic)
    setShowDeleteModal(true)
  }

  const confirmDeleteTopic = async () => {
    if (!topicToDelete) return

    setDeleting(true)
    try {
      await deleteTopic(topicToDelete.id)
      setShowDeleteModal(false)
      setTopicToDelete(null)
    } catch (err) {
      console.error('Error deleting topic:', err)
      alert('Failed to delete topic. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  const cancelDeleteTopic = () => {
    setShowDeleteModal(false)
    setTopicToDelete(null)
  }

  const handleMarkAsUsed = async (topicId) => {
    try {
      await markTopicAsUsed(topicId)
    } catch (err) {
      console.error('Error marking topic as used:', err)
      alert('Failed to mark topic as used. Please try again.')
    }
  }

  const handleMarkAsUnused = async (topicId) => {
    if (!window.confirm('Are you sure you want to mark this topic as unused? This will allow it to be reused.')) {
      return
    }

    try {
      await markTopicAsUnused(topicId)
    } catch (err) {
      console.error('Error marking topic as unused:', err)
      alert('Failed to mark topic as unused. Please try again.')
    }
  }

  // Initialize editing fields when topic is selected
  useEffect(() => {
    if (selectedTopic) {
      setEditingFields({
        filename: selectedTopic.filename || '',
        htmloutput: selectedTopic.htmloutput || '',
        keywords: Array.isArray(selectedTopic.keywords) 
          ? selectedTopic.keywords.join(', ') 
          : (selectedTopic.keywords || ''),
        description: selectedTopic.description || ''
      })
      setFieldError(null)
    }
  }, [selectedTopic])

  // Scroll HTML preview to top when switching between topics (no page scroll)
  useEffect(() => {
    if (!selectedTopic?.id) return
    const el = htmlPreviewRef.current
    if (el) {
      el.scrollTop = 0
    }
  }, [selectedTopic?.id])

  const handleFieldChange = (field, value) => {
    setEditingFields(prev => ({
      ...prev,
      [field]: value
    }))
    setFieldError(null)
  }

  // Suggested product URL for "no links" case: prefer topic's category product, else first product with url
  const getSuggestedProductUrl = () => {
    if (!selectedTopic || !products?.length) return null
    const topicProduct = selectedTopic.category ? products.find(p => p.id === selectedTopic.category) : null
    const withUrl = (topicProduct?.url && topicProduct.url.trim()) ? topicProduct : products.find(p => p.url && String(p.url).trim())
    if (!withUrl?.url || !String(withUrl.url).trim()) return null
    return { url: String(withUrl.url).trim(), productName: withUrl.name || 'Product' }
  }

  const handleAddSuggestedUrl = (url) => {
    const hrefEscaped = url.replace(/&/g, '&amp;').replace(/"/g, '&quot;')
    const textEscaped = url.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
    const snippet = `\n<p>More information: <a href="${hrefEscaped}">${textEscaped}</a></p>`
    setEditingFields(prev => ({ ...prev, htmloutput: (prev.htmloutput || '') + snippet }))
    setFieldError(null)
  }

  const handleSaveFields = async () => {
    if (!selectedTopic) return

    setSavingFields(true)
    setFieldError(null)

    try {
      const updates = {}
      
      // Prepare updates
      if (editingFields.filename !== (selectedTopic.filename || '')) {
        updates.filename = editingFields.filename.trim()
      }
      
      if (editingFields.htmloutput !== (selectedTopic.htmloutput || '')) {
        updates.htmloutput = editingFields.htmloutput
      }
      
      // Handle keywords - convert comma-separated string to array
      const keywordsArray = editingFields.keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0)
      const currentKeywords = Array.isArray(selectedTopic.keywords) 
        ? selectedTopic.keywords 
        : (selectedTopic.keywords ? [selectedTopic.keywords] : [])
      
      // Compare arrays
      const keywordsChanged = JSON.stringify(keywordsArray.sort()) !== JSON.stringify(currentKeywords.sort())
      if (keywordsChanged) {
        updates.keywords = keywordsArray
      }
      
      if (editingFields.description !== (selectedTopic.description || '')) {
        updates.description = editingFields.description.trim()
      }

      if (Object.keys(updates).length === 0) {
        setSavingFields(false)
        return
      }

      await updateTopic(selectedTopic.id, updates)
      
      // Update local state to reflect changes
      const updatedTopic = {
        ...selectedTopic,
        ...updates
      }
      setSelectedTopic(updatedTopic)
      
    } catch (err) {
      console.error('Error saving fields:', err)
      setFieldError(err.message || 'Failed to save changes. Please try again.')
    } finally {
      setSavingFields(false)
    }
  }

  const canEdit = (isEditor || isAdmin) && selectedTopic?.status === 'available'

  const handleApprove = async () => {
    if (!selectedTopic) return
    
    if (!window.confirm('Are you sure you want to approve this topic? This will set the status to "Approved".')) {
      return
    }

    try {
      await updateTopic(selectedTopic.id, { status: 'approved' })
      
      // Create sitemap entry
      if (selectedTopic.updatedAt && selectedTopic.filename) {
        // Format updatedAt to YYYY-MM-DD and get year
        const date = selectedTopic.updatedAt.toDate ? selectedTopic.updatedAt.toDate() : new Date(selectedTopic.updatedAt)
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear().toString()
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          const lastmod = `${year}-${month}-${day}`
          
          // Add entry to sitemap/{year}/entries subcollection
          const yearDocRef = doc(db, 'sitemap', year)
          const entriesRef = collection(yearDocRef, 'entries')
          await addDoc(entriesRef, {
            lastmod: lastmod,
            filename: selectedTopic.filename
          })
        }
      }
      
      // Update local state to reflect changes
      const updatedTopic = {
        ...selectedTopic,
        status: 'approved'
      }
      setSelectedTopic(updatedTopic)
    } catch (err) {
      console.error('Error approving topic:', err)
      alert('Failed to approve topic. Please try again.')
    }
  }

  const handleReject = async () => {
    if (!selectedTopic) return
    
    if (!window.confirm('Are you sure you want to reject this topic? This will set the status to "Rejected".')) {
      return
    }

    try {
      await updateTopic(selectedTopic.id, { status: 'rejected' })
      
      // Update local state to reflect changes
      const updatedTopic = {
        ...selectedTopic,
        status: 'rejected'
      }
      setSelectedTopic(updatedTopic)
    } catch (err) {
      console.error('Error rejecting topic:', err)
      alert('Failed to reject topic. Please try again.')
    }
  }

  // Shared import logic for both file and text imports
  const processTopicsImport = async (topicsText) => {
    // Check if category is selected
    if (!importCategory) {
      setTopicError('Please select a category before importing topics.')
      return
    }

    setImporting(true)
    setImportResult(null)
    setTopicError(null)

    try {
      // Parse topics: one per line, trim trailing spaces, filter empty lines
      const lines = topicsText.split('\n')
      const importedTopics = lines
        .map(line => {
          // Trim trailing spaces only (preserve leading spaces if any)
          return line.replace(/\s+$/, '')
        })
        .filter(line => line.length > 0) // Remove empty lines
      
      if (importedTopics.length === 0) {
        setImportResult({
          success: false,
          message: 'No valid topics found. Each topic should be on a separate line.'
        })
        setImporting(false)
        return
      }

      // Create a map for duplicate checking: text + category combination
      // A duplicate exists only if both text AND category match
      const existingTopicsMap = new Map()
      topics.forEach(topic => {
        const normalizedText = topic.text.trim().toLowerCase()
        const category = topic.category || null
        const key = `${normalizedText}|${category}`
        existingTopicsMap.set(key, true)
      })

      // Filter out duplicates and normalize new topics
      const uniqueTopics = []
      const duplicates = []
      const seenInFile = new Set()
      const importCategoryId = importCategory || null

      for (const topicText of importedTopics) {
        // For duplicate checking, normalize by trimming both ends and lowercasing
        const normalized = topicText.trim().toLowerCase()
        const key = `${normalized}|${importCategoryId}`
        
        // Check for duplicates within the import itself
        if (seenInFile.has(normalized)) {
          duplicates.push(topicText)
          continue
        }
        seenInFile.add(normalized)

        // Check for duplicates with existing topics (same text AND same category)
        if (existingTopicsMap.has(key)) {
          duplicates.push(topicText)
          continue
        }

        // Store the topic with trailing spaces already trimmed
        // createTopic will trim both ends when saving
        uniqueTopics.push(topicText)
      }

      // Import unique topics
      let importedCount = 0
      let failedCount = 0

      // Use the selected import category (or null if none selected)
      const categoryId = importCategory || null

      for (const topicText of uniqueTopics) {
        try {
          // Import with selected category
          await createTopic(topicText, categoryId)
          importedCount++
        } catch (err) {
          console.error('Error importing topic:', topicText, err)
          failedCount++
        }
      }

      // Show results
      const duplicateCount = duplicates.length
      setImportResult({
        success: true,
        imported: importedCount,
        duplicates: duplicateCount,
        failed: failedCount,
        message: `Import completed: ${importedCount} topics imported${duplicateCount > 0 ? `, ${duplicateCount} duplicates skipped` : ''}${failedCount > 0 ? `, ${failedCount} failed` : ''}.`
      })

      // Clear result after 5 seconds
      setTimeout(() => setImportResult(null), 5000)
      
      // Clear text import if it was used
      if (showTextImport) {
        setImportText('')
      }
    } catch (err) {
      console.error('Error importing topics:', err)
      setImportResult({
        success: false,
        message: 'Failed to import topics. Please try again.'
      })
    } finally {
      setImporting(false)
    }
  }

  const handleFileImport = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Reset file input
    e.target.value = ''

    try {
      const text = await file.text()
      await processTopicsImport(text)
    } catch (err) {
      console.error('Error reading file:', err)
      setImportResult({
        success: false,
        message: 'Failed to read file. Please ensure it is a valid text file.'
      })
    }
  }

  const handleTextImport = async () => {
    if (!importText.trim()) {
      setTopicError('Please enter at least one topic.')
      return
    }
    await processTopicsImport(importText)
  }

  const handleImportTextChange = (e) => {
    const value = e.target.value
    // Normalize line breaks to \n (handle Windows \r\n and old Mac \r)
    const normalizedValue = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    // Limit to 100 lines
    const lines = normalizedValue.split('\n')
    if (lines.length <= 100) {
      setImportText(normalizedValue)
      setTopicError(null)
    } else {
      setTopicError('Maximum 100 lines allowed. Please reduce the number of topics.')
    }
  }

  const handleImportTextPaste = async (e) => {
    // Prevent default paste behavior
    e.preventDefault()
    
    // Get pasted text from clipboard
    const pastedText = (e.clipboardData || window.clipboardData).getData('text')
    
    // Normalize line breaks - handle various formats
    // Mac Notes and other apps might use different line break characters
    let normalizedText = pastedText
      .replace(/\r\n/g, '\n')  // Windows
      .replace(/\r/g, '\n')     // Old Mac
      .replace(/\u2028/g, '\n') // Line separator
      .replace(/\u2029/g, '\n') // Paragraph separator
    
    // Get current text and cursor position
    const textarea = e.target
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const currentText = importText
    
    // Insert pasted text at cursor position
    const newText = currentText.substring(0, start) + normalizedText + currentText.substring(end)
    
    // Check line limit
    const lines = newText.split('\n')
    if (lines.length <= 100) {
      setImportText(newText)
      setTopicError(null)
      
      // Set cursor position after pasted text
      setTimeout(() => {
        const newCursorPos = start + normalizedText.length
        textarea.setSelectionRange(newCursorPos, newCursorPos)
      }, 0)
    } else {
      setTopicError('Maximum 100 lines allowed. Please reduce the number of topics.')
    }
  }

  if (loading) {
    return (
      <div className="website-articles-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  if (!isEditor) {
    return (
      <div className="website-articles-container">
        <div className="website-articles-access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="website-articles-container">
      <div className="website-articles-header">
        <h1>Filesig Website Articles</h1>
        <p className="website-articles-subtitle">Manage and create website articles</p>
      </div>
      
      <div className="website-articles-tabs">
        <button
          className={`website-articles-tab ${activeTab === 'articles' ? 'active' : ''}`}
          onClick={() => setActiveTab('articles')}
        >
          Articles
        </button>
        <button
          className={`website-articles-tab ${activeTab === 'topics' ? 'active' : ''}`}
          onClick={() => setActiveTab('topics')}
        >
          Topics
        </button>
        {(isEditor || isAdmin) && (
          <button
            className={`website-articles-tab ${activeTab === 'sitemap' ? 'active' : ''}`}
            onClick={() => setActiveTab('sitemap')}
          >
            Sitemap List
          </button>
        )}
      </div>
      
      <div className="website-articles-content">
        {activeTab === 'articles' && (
          <div className="articles-layout">
            <div className="articles-sidebar">
              <h3 className="sidebar-title">Available Topics</h3>
              <div className="sidebar-product-filter">
                <label htmlFor="articles-product-filter" className="filter-label">Filter by product</label>
                <select
                  id="articles-product-filter"
                  className="filter-select"
                  value={articlesProductFilter}
                  onChange={(e) => setArticlesProductFilter(e.target.value)}
                >
                  <option value="">All products</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
              {availableTopicsList.length === 0 ? (
                <div className="sidebar-empty">
                  <p>
                    {articlesProductFilter
                      ? 'No available topics for this product'
                      : 'No available topics'}
                  </p>
                </div>
              ) : (
                <ul className="topics-list-sidebar">
                  {availableTopicsList.map((topic) => (
                    <li 
                      key={topic.id} 
                      className={`topic-item-sidebar ${selectedTopic?.id === topic.id ? 'selected' : ''}`}
                      onClick={() => setSelectedTopic(topic)}
                    >
                      <div className="topic-text-sidebar">{topic.text}</div>
                      {topic.category && (
                        <div className="topic-category-sidebar">
                          {(() => {
                            const product = products.find(p => p.id === topic.category)
                            return product ? product.name : 'Unknown'
                          })()}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="articles-main">
              {selectedTopic ? (
                <div className="topic-details-view">
                  <div className="topic-details-header">
                    <h2>Topic Details</h2>
                    <button 
                      className="btn btn-secondary btn-sm"
                      onClick={() => setSelectedTopic(null)}
                    >
                      Clear Selection
                    </button>
                  </div>
                  <div className="topic-details-content">
                    <div className="detail-row">
                      <label className="detail-label">Status</label>
                      <div className="detail-value">
                        {selectedTopic.status === 'used' ? (
                          <span className="status-badge used-badge">Used</span>
                        ) : selectedTopic.status === 'available' ? (
                          <span className="status-badge available-badge">Available</span>
                        ) : selectedTopic.status === 'approved' ? (
                          <span className="status-badge approved-badge">Approved</span>
                        ) : selectedTopic.status === 'published' ? (
                          <span className="status-badge approved-badge">Published</span>
                        ) : selectedTopic.status === 'rejected' ? (
                          <span className="status-badge rejected-badge">Rejected</span>
                        ) : selectedTopic.status === 'error' ? (
                          <span className="status-badge rejected-badge">Error</span>
                        ) : selectedTopic.status === 'pre-processing' ? (
                          <span className="status-badge">Pre-Processing</span>
                        ) : selectedTopic.status === 'unprocessed' ? (
                          <span className="status-badge unprocessed-badge">Unprocessed</span>
                        ) : (
                          <span className="status-badge" title={`Unknown status: ${selectedTopic.status}`}>{selectedTopic.status || 'Unprocessed'}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="detail-row">
                      <label className="detail-label">Topic Text</label>
                      <div className="detail-value">{selectedTopic.text || 'N/A'}</div>
                    </div>
                    
                    <div className="detail-row">
                      <label className="detail-label">Category</label>
                      <div className="detail-value">
                        {selectedTopic.category ? (
                          (() => {
                            const product = products.find(p => p.id === selectedTopic.category)
                            return product ? product.name : 'Unknown'
                          })()
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>No category</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="detail-row">
                      <label className="detail-label">Created At</label>
                      <div className="detail-value">
                        {selectedTopic.createdAt && selectedTopic.createdAt.toDate
                          ? new Date(selectedTopic.createdAt.toDate()).toLocaleString()
                          : 'N/A'}
                      </div>
                    </div>
                    
                    {selectedTopic.updatedAt && (
                      <div className="detail-row">
                        <label className="detail-label">Updated At</label>
                        <div className="detail-value">
                          {selectedTopic.updatedAt.toDate
                            ? new Date(selectedTopic.updatedAt.toDate()).toLocaleString()
                            : 'N/A'}
                        </div>
                      </div>
                    )}
                    
                    {selectedTopic.processedAt && (
                      <div className="detail-row">
                        <label className="detail-label">Processed At</label>
                        <div className="detail-value">
                          {selectedTopic.processedAt.toDate
                            ? new Date(selectedTopic.processedAt.toDate()).toLocaleString()
                            : 'N/A'}
                        </div>
                      </div>
                    )}
                    
                    <div className="detail-row">
                      <label className="detail-label">Filename</label>
                      {canEdit ? (
                        <input
                          type="text"
                          className="detail-input"
                          value={editingFields.filename}
                          onChange={(e) => handleFieldChange('filename', e.target.value)}
                          placeholder="Enter filename"
                        />
                      ) : (
                        <div className="detail-value">{selectedTopic.filename || 'N/A'}</div>
                      )}
                    </div>
                    
                    {(canEdit || selectedTopic.keywords !== undefined) && (
                      <div className="detail-row detail-row-full">
                        <label className="detail-label">Keywords</label>
                        {canEdit ? (
                          <textarea
                            className="detail-textarea"
                            value={editingFields.keywords}
                            onChange={(e) => handleFieldChange('keywords', e.target.value)}
                            placeholder="Enter keywords separated by commas"
                            rows={3}
                          />
                        ) : (
                          <div className="detail-value">
                            {Array.isArray(selectedTopic.keywords) 
                              ? selectedTopic.keywords.join(', ') 
                              : (selectedTopic.keywords || 'N/A')}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {(canEdit || selectedTopic.description !== undefined) && (
                      <div className="detail-row detail-row-full">
                        <label className="detail-label">Description</label>
                        {canEdit ? (
                          <textarea
                            className="detail-textarea"
                            value={editingFields.description}
                            onChange={(e) => handleFieldChange('description', e.target.value)}
                            placeholder="Enter description"
                            rows={3}
                          />
                        ) : (
                          <div className="detail-value">{selectedTopic.description || 'N/A'}</div>
                        )}
                      </div>
                    )}
                    
                    {(canEdit || selectedTopic.htmloutput) && (
                      <div className="detail-row detail-row-full">
                        <label className="detail-label">HTML Output</label>
                        {canEdit ? (
                          <>
                            {editingFields.htmloutput && (
                              <div className="html-preview-section">
                                <div className="html-preview-header">
                                  <span>Preview</span>
                                </div>
                                <div ref={htmlPreviewRef} className="detail-value detail-value-textarea html-preview">
                                  <div 
                                    className="detail-html-content" 
                                    dangerouslySetInnerHTML={{ __html: editingFields.htmloutput }}
                                  />
                                </div>
                              </div>
                            )}
                            <div className="html-code-section">
                              <div className="html-code-header">
                                <span>HTML Code</span>
                              </div>
                              <textarea
                                className="detail-textarea detail-textarea-code"
                                value={editingFields.htmloutput}
                                onChange={(e) => handleFieldChange('htmloutput', e.target.value)}
                                placeholder="Enter HTML content"
                              />
                            </div>
                            {editingFields.htmloutput && (() => {
                              const hrefs = getHrefsFromHtml(editingFields.htmloutput)
                              const suggested = getSuggestedProductUrl()
                              if (hrefs.length > 0) {
                                return (
                                  <div className="html-links-section">
                                    <div className="html-links-header">
                                      <span>Links in this HTML</span>
                                    </div>
                                    <ul className="html-links-list">
                                      {hrefs.map((url, i) => (
                                        <li key={`${url}-${i}`}>
                                          <a href={url} target="_blank" rel="noopener noreferrer" className="html-link-item" title={url}>
                                            {url.length > 80 ? `${url.slice(0, 80)}…` : url}
                                          </a>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )
                              }
                              if (suggested) {
                                return (
                                  <div className="html-links-section html-links-suggested">
                                    <div className="html-links-header">
                                      <span>Links in this HTML</span>
                                    </div>
                                    <p className="html-links-suggested-note">This is suggested.</p>
                                    <div className="html-links-suggested-row">
                                      <a href={suggested.url} target="_blank" rel="noopener noreferrer" className="html-link-item" title={suggested.url}>
                                        {suggested.url.length > 80 ? `${suggested.url.slice(0, 80)}…` : suggested.url}
                                      </a>
                                      <button
                                        type="button"
                                        className="btn btn-secondary btn-sm html-add-url-btn"
                                        onClick={() => handleAddSuggestedUrl(suggested.url)}
                                      >
                                        Add URL
                                      </button>
                                    </div>
                                    <p className="html-links-suggested-hint">Click Add URL to append “More information: [link]” at the end of the HTML.</p>
                                  </div>
                                )
                              }
                              return null
                            })()}
                          </>
                        ) : (
                          <>
                            <div className="html-preview-section">
                              <div className="html-preview-header">
                                <span>Preview</span>
                              </div>
                              <div ref={htmlPreviewRef} className="detail-value detail-value-textarea html-preview">
                                <div 
                                  className="detail-html-content" 
                                  dangerouslySetInnerHTML={{ __html: selectedTopic.htmloutput }}
                                />
                              </div>
                            </div>
                            <div className="html-code-section">
                              <div className="html-code-header">
                                <span>HTML Code</span>
                              </div>
                              <div className="detail-value detail-value-textarea">
                                <pre className="detail-textarea-content">{selectedTopic.htmloutput}</pre>
                              </div>
                            </div>
                            {selectedTopic.htmloutput && (() => {
                              const hrefs = getHrefsFromHtml(selectedTopic.htmloutput)
                              const suggested = getSuggestedProductUrl()
                              if (hrefs.length > 0) {
                                return (
                                  <div className="html-links-section">
                                    <div className="html-links-header">
                                      <span>Links in this HTML</span>
                                    </div>
                                    <ul className="html-links-list">
                                      {hrefs.map((url, i) => (
                                        <li key={`${url}-${i}`}>
                                          <a href={url} target="_blank" rel="noopener noreferrer" className="html-link-item" title={url}>
                                            {url.length > 80 ? `${url.slice(0, 80)}…` : url}
                                          </a>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )
                              }
                              if (suggested) {
                                return (
                                  <div className="html-links-section html-links-suggested">
                                    <div className="html-links-header">
                                      <span>Links in this HTML</span>
                                    </div>
                                    <p className="html-links-suggested-note">This is suggested.</p>
                                    <a href={suggested.url} target="_blank" rel="noopener noreferrer" className="html-link-item" title={suggested.url}>
                                      {suggested.url.length > 80 ? `${suggested.url.slice(0, 80)}…` : suggested.url}
                                    </a>
                                    <p className="html-links-suggested-hint">In edit mode you can click Add URL to append “More information: [link]” at the end of the HTML.</p>
                                  </div>
                                )
                              }
                              return null
                            })()}
                          </>
                        )}
                      </div>
                    )}
                    
                    {canEdit && (
                      <>
                        {fieldError && (
                          <div className="detail-row detail-row-full">
                            <div className="form-error">{fieldError}</div>
                          </div>
                        )}
                        <div className="detail-row detail-row-full">
                          <div className="detail-actions">
                            <button
                              className="btn btn-primary"
                              onClick={handleSaveFields}
                              disabled={savingFields}
                            >
                              {savingFields ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button
                              className="btn btn-success"
                              onClick={handleApprove}
                              disabled={savingFields}
                            >
                              Approve
                            </button>
                            <button
                              className="btn btn-danger"
                              onClick={handleReject}
                              disabled={savingFields}
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                    
                    {selectedTopic.error && (
                      <div className="detail-row detail-row-full">
                        <label className="detail-label">Error</label>
                        <div className="detail-value detail-value-error">{selectedTopic.error}</div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="website-articles-placeholder">
                  <div className="placeholder-icon">📝</div>
                  <h2>Filesig Website Articles</h2>
                  <p>Select a topic from the list to view its details.</p>
                  <p className="placeholder-note">All fields will be displayed in read-only mode for review.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'topics' && (
          <div className="topics-section">
            <div className="topics-header">
              <h2>Topics</h2>
              <div className="topics-header-actions">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginRight: '0.75rem' }}>
                  <label htmlFor="import-category" style={{ fontSize: '14px', color: '#666' }}>
                    Import Category: <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <select
                    id="import-category"
                    value={importCategory}
                    onChange={(e) => {
                      setImportCategory(e.target.value)
                      setTopicError(null) // Clear error when category is selected
                    }}
                    disabled={importing}
                    required
                    style={{
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: importCategory ? '1px solid #ddd' : '1px solid #dc3545',
                      fontSize: '14px',
                      minWidth: '150px'
                    }}
                  >
                    <option value="">Select category...</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowTextImport(!showTextImport)}
                  disabled={importing}
                  style={{ marginRight: '0.75rem' }}
                >
                  {showTextImport ? '📁 Import from File' : '📝 Import from Text'}
                </button>
                {!showTextImport ? (
                  <label 
                    className="btn btn-secondary"
                    htmlFor="topic-import-file" 
                    style={{ 
                      marginRight: '0.75rem',
                      opacity: importCategory ? 1 : 0.6,
                      cursor: importCategory ? 'pointer' : 'not-allowed',
                      position: 'relative'
                    }}
                    title={!importCategory ? 'Please select a category first' : ''}
                  >
                    {importing ? 'Importing...' : '📥 Import Topics'}
                    <input
                      id="topic-import-file"
                      type="file"
                      accept=".txt,.text"
                      onChange={handleFileImport}
                      disabled={importing || !importCategory}
                      style={{ display: 'none' }}
                    />
                  </label>
                ) : (
                  <button
                    className="btn btn-secondary"
                    onClick={handleTextImport}
                    disabled={importing || !importCategory || !importText.trim()}
                    style={{ 
                      marginRight: '0.75rem',
                      opacity: (importCategory && importText.trim()) ? 1 : 0.6,
                      cursor: (importCategory && importText.trim()) ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {importing ? 'Importing...' : '📥 Import Topics'}
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowTopicForm(!showTopicForm)
                    if (showTopicForm) {
                      handleCancelEdit()
                    }
                  }}
                  disabled={submitting || importing}
                >
                  {showTopicForm ? 'Cancel' : '+ Add Topic'}
                </button>
              </div>
            </div>

            {showTextImport && (
              <div className="text-import-container" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                <div className="form-group">
                  <label htmlFor="import-text">
                    Enter Topics (one per line, max 100 lines)
                    <span className="form-help-text">
                      Paste or type your topics here, one topic per line
                    </span>
                  </label>
                  <textarea
                    id="import-text"
                    value={importText}
                    onChange={handleImportTextChange}
                    onPaste={handleImportTextPaste}
                    placeholder="Enter topics, one per line...&#10;Example:&#10;Introduction to cybersecurity&#10;Advanced threat detection&#10;Network security basics"
                    rows={10}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      resize: 'vertical',
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word'
                    }}
                    disabled={importing}
                  />
                  <div style={{ marginTop: '0.5rem', fontSize: '12px', color: '#666' }}>
                    {importText.split('\n').filter(line => line.trim()).length} / 100 lines
                  </div>
                </div>
              </div>
            )}

            {importResult && (
              <div className={`import-result ${importResult.success ? 'success' : 'error'}`}>
                {importResult.message}
              </div>
            )}

            {/* Stats */}
            <div className="topics-stats">
              <div className="stat-item">
                <span className="stat-label">
                  Total Topics:
                  <span className="stat-tooltip" title="The total number of topics in the system">
                    <span className="tooltip-icon">?</span>
                    <span className="tooltip-text">The total number of topics in the system</span>
                  </span>
                </span>
                <span className="stat-value">{totalTopics}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">
                  Unprocessed:
                  <span className="stat-tooltip" title="Topics that have been created but not yet processed">
                    <span className="tooltip-icon">?</span>
                    <span className="tooltip-text">Topics that have been created but an article has not been generated yet</span>
                  </span>
                </span>
                <span className="stat-value unprocessed">{unprocessedTopics}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">
                  Pre-Processing:
                  <span className="stat-tooltip" title="Topics currently being processed by the system">
                    <span className="tooltip-icon">?</span>
                    <span className="tooltip-text">Topics currently being awaiting article cleaning and formatting</span>
                  </span>
                </span>
                <span className="stat-value">{preProcessingTopics}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">
                  Available:
                  <span className="stat-tooltip" title="Topics that are ready for editing and review. These can be selected to view details and make changes.">
                    <span className="tooltip-icon">?</span>
                    <span className="tooltip-text">Topics that are ready for editing, review and approval.</span>
                  </span>
                </span>
                <span className="stat-value available">{availableTopics}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">
                  Approved:
                  <span className="stat-tooltip" title="Topics that have been reviewed and approved for publication">
                    <span className="tooltip-icon">?</span>
                    <span className="tooltip-text">Topics that have been reviewed and approved and awaiting publication</span>
                  </span>
                </span>
                <span className="stat-value approved">{approvedTopics}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">
                  Published:
                  <span className="stat-tooltip" title="Topics that have been published and are live on the website. These appear in the sitemap.">
                    <span className="tooltip-icon">?</span>
                    <span className="tooltip-text">Topics that have been published and are live on the website. These appear in the sitemap.</span>
                  </span>
                </span>
                <span className="stat-value approved">{publishedTopics.length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">
                  Rejected:
                  <span className="stat-tooltip" title="Topics that have been reviewed and rejected. They will not be published.">
                    <span className="tooltip-icon">?</span>
                    <span className="tooltip-text">Topics that have been reviewed and rejected. They will not be published.</span>
                  </span>
                </span>
                <span className="stat-value rejected">{rejectedTopics}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">
                  Error:
                  <span className="stat-tooltip" title="Topics that encountered an error during processing. These may need attention or re-processing.">
                    <span className="tooltip-icon">?</span>
                    <span className="tooltip-text">Topics that encountered an error during processing. These may need attention or re-processing.</span>
                  </span>
                </span>
                <span className="stat-value rejected">{errorTopics}</span>
              </div>
            </div>

            {showTopicForm && (
              <div className="topic-form-container">
                <form onSubmit={handleTopicSubmit} className="topic-form">
                  <div className="form-group">
                    <label htmlFor="topic-text">
                      Topic Text *
                      <span className="form-help-text">
                        Enter a simple sentence describing the topic
                      </span>
                    </label>
                    <textarea
                      id="topic-text"
                      name="topicText"
                      value={topicText}
                      onChange={handleTopicFormChange}
                      placeholder="Enter topic text (e.g., 'Introduction to cybersecurity')"
                      rows="3"
                      required
                      disabled={submitting}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="topic-category">
                      Category
                      <span className="form-help-text">
                        Select a product category for this topic (optional)
                      </span>
                    </label>
                    <select
                      id="topic-category"
                      name="topicCategory"
                      value={topicCategory}
                      onChange={handleTopicFormChange}
                      disabled={submitting}
                    >
                      <option value="">No category</option>
                      {products.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {topicError && (
                    <div className="form-error">
                      {topicError}
                    </div>
                  )}

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? 'Saving...' : editingTopic ? 'Update Topic' : 'Create Topic'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="topics-list">
              {topics.length === 0 ? (
                <div className="empty-state">
                  <p>No topics yet. Click "Add Topic" to create one.</p>
                </div>
              ) : (
                <>
                  <table className="topics-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Topic Text</th>
                        <th>Category</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentPageTopics.map((topic) => {
                        const status = topic.status || 'unprocessed'
                        return (
                        <tr
                          key={topic.id}
                          className={status === 'used' ? 'used' : ''}
                        >
                          <td>
                            {status === 'used' ? (
                              <span className="status-badge used-badge">Used</span>
                            ) : status === 'available' ? (
                              <span className="status-badge available-badge">Available</span>
                            ) : status === 'approved' ? (
                              <span className="status-badge approved-badge">Approved</span>
                            ) : status === 'published' ? (
                              <span className="status-badge approved-badge">Published</span>
                            ) : status === 'rejected' ? (
                              <span className="status-badge rejected-badge">Rejected</span>
                            ) : status === 'error' ? (
                              <span className="status-badge rejected-badge">Error</span>
                            ) : status === 'pre-processing' ? (
                              <span className="status-badge">Pre-Processing</span>
                            ) : status === 'unprocessed' ? (
                              <span className="status-badge unprocessed-badge">Unprocessed</span>
                            ) : (
                              <span className="status-badge" title={`Unknown status: ${status}`}>{status || 'Unprocessed'}</span>
                            )}
                          </td>
                        <td className="topic-text-cell">
                          {topic.text}
                        </td>
                        <td className="topic-category-cell">
                          {topic.category ? (
                            (() => {
                              const product = products.find(p => p.id === topic.category)
                              return product ? product.name : 'Unknown'
                            })()
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>No category</span>
                          )}
                        </td>
                        <td className="topic-date-cell">
                          {topic.createdAt && topic.createdAt.toDate
                            ? new Date(topic.createdAt.toDate()).toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td className="topic-actions-cell">
                          <div className="topic-actions">
                            {status !== 'used' && (
                              <>
                                <button
                                  className="btn-icon"
                                  onClick={() => handleStartEdit(topic)}
                                  title="Edit topic"
                                >
                                  ✏️
                                </button>
                              </>
                            )}
                            {isAdmin && (
                              <>
                                {status === 'used' && (
                                  <button
                                    className="btn-icon"
                                    onClick={() => handleMarkAsUnused(topic.id)}
                                    title="Mark as available (Admin only)"
                                  >
                                    ↶
                                  </button>
                                )}
                                <button
                                  className="btn-icon"
                                  onClick={() => handleDeleteTopic(topic)}
                                  title="Delete topic (Admin only)"
                                >
                                  🗑️
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      )
                      })}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="topics-pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <div className="pagination-info">
                        Page {currentPage} of {totalPages}
                        <span className="pagination-count">
                          (Showing {startIndex + 1}-{Math.min(endIndex, totalTopics)} of {totalTopics})
                        </span>
                      </div>
                      <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'sitemap' && (isEditor || isAdmin) && (
          <div className="sitemap-section">
            <div className="sitemap-header">
              <h2>Sitemap List</h2>
              <div className="sitemap-filter">
                <label htmlFor="year-filter" style={{ marginRight: '0.75rem', fontSize: '14px', color: '#666' }}>
                  Filter by Year:
                </label>
                <select
                  id="year-filter"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  style={{
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    minWidth: '120px'
                  }}
                >
                  {getAvailableYears().map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="sitemap-table-container">
              {getSitemapTopics().length === 0 ? (
                <div className="empty-state">
                  <p>No published topics found for {selectedYear}.</p>
                </div>
              ) : (
                <>
                  {getSitemapTopics().length > 25 && (
                    <p className="sitemap-limit-note" style={{ marginBottom: '0.75rem', fontSize: '13px', color: '#666' }}>
                      Showing the 25 most recently processed (of {getSitemapTopics().length} for {selectedYear}).
                    </p>
                  )}
                <table className="sitemap-table">
                  <thead>
                    <tr>
                      <th>Filename</th>
                      <th>Updated At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSitemapTopics().slice(0, 25).map((topic) => (
                      <tr key={topic.id}>
                        <td className="sitemap-filename-cell">
                          {topic.filename || 'N/A'}
                        </td>
                        <td className="sitemap-date-cell">
                          {formatDate(topic.updatedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={cancelDeleteTopic}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Topic</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this topic?</p>
              {topicToDelete && (
                <div className="topic-to-delete">
                  <strong>"{topicToDelete.text}"</strong>
                </div>
              )}
              <p className="modal-warning">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={cancelDeleteTopic}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmDeleteTopic}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default WebsiteArticles
